import { useEffect, useState } from "react";
import { Plus, Loader2, Tag } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function AdminRoles() {
  const { refreshRolesCatalog } = useAuth();
  const [roles, setRoles] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/roles?include_inactive=true").then(({ data }) => setRoles(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/admin/roles", { label });
      setLabel("");
      load();
      refreshRolesCatalog();
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const toggleActif = async (r) => {
    await api.put(`/admin/roles/${r.value}`, { actif: !r.actif });
    load();
    refreshRolesCatalog();
  };

  const builtIn = roles.filter((r) => r.built_in);
  const custom = roles.filter((r) => !r.built_in);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-800 bg-[#121820] p-4 space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-300 flex items-center gap-2">
          <Tag className="w-4 h-4 text-cyan-400" /> Ajouter un rôle personnalisé
        </h3>
        <p className="text-xs text-slate-500">
          Un rôle personnalisé est un simple intitulé de métier (ex: Grutier) attribuable aux utilisateurs
          en plus des rôles existants. Il ne donne aucun droit particulier — seuls RESPONSABLE et
          ADMINISTRATEUR conservent les permissions d'administration.
        </p>
        <div className="flex gap-2">
          <Input
            data-testid="admin-roles-label-input"
            placeholder="Intitulé du métier (ex: Grutier)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="bg-[#0A0D12] border-slate-700 text-slate-100"
          />
          <Button data-testid="admin-roles-submit-button" disabled={busy || !label.trim()} onClick={create} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs whitespace-nowrap">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Créer</>}
          </Button>
        </div>
        {error && <div className="text-sm text-red-400">{error}</div>}
      </div>

      <div className="space-y-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500">Rôles système (non modifiables)</h3>
        <div className="flex flex-wrap gap-2">
          {builtIn.map((r) => (
            <span key={r.value} data-testid={`admin-role-built-in-${r.value}`} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-700 text-slate-400">
              {r.label}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500">Rôles personnalisés</h3>
        {custom.length === 0 && <p className="text-sm text-slate-500">Aucun rôle personnalisé créé.</p>}
        {custom.map((r) => (
          <div key={r.value} data-testid={`admin-role-row-${r.value}`} className={`rounded-lg border border-slate-800 bg-[#121820] p-3 flex items-center justify-between ${r.actif ? "" : "opacity-50"}`}>
            <div className="text-sm font-semibold text-slate-100">
              {r.label} <span className="text-slate-500 font-mono text-xs ml-1">({r.value})</span>
              {!r.actif && <span data-testid={`admin-role-inactive-badge-${r.value}`} className="text-slate-500 text-xs ml-2 border border-slate-700 rounded px-1.5 py-0.5">Désactivé</span>}
            </div>
            <Button data-testid={`admin-role-toggle-${r.value}`} size="sm" variant="outline" onClick={() => toggleActif(r)} className="border-slate-700 text-xs">
              {r.actif ? "Désactiver" : "Activer"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
